import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { LayoutService } from '../../../layout/service/layout.service';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'pt-theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly layoutService = inject(LayoutService);
  private readonly _mode = signal<ThemeMode>(this.loadSavedTheme());

  readonly mode = computed(() => this._mode());
  readonly isDark = computed(() => this._mode() === 'dark');

  constructor() {
    effect(() => {
      const mode = this._mode();
      this.applyTheme(mode);
      this.layoutService.layoutConfig.update((c) => ({
        ...c,
        darkTheme: mode === 'dark',
      }));
    });
  }

  setTheme(mode: ThemeMode): void {
    this._mode.set(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }

  toggle(): void {
    this.setTheme(this._mode() === 'dark' ? 'light' : 'dark');
  }

  private loadSavedTheme(): ThemeMode {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }

  private applyTheme(mode: ThemeMode): void {
    const html = document.documentElement;
    html.setAttribute('data-theme', mode);
    if (mode === 'dark') {
      html.classList.add('app-dark');
    } else {
      html.classList.remove('app-dark');
    }
  }
}
